import { Box, Stack, Typography } from '@mui/material';
import { ElementType } from 'react';
import { FormattedMessage } from 'react-intl';

import { replacePathParams } from 'utils/routes.utils';
import { aspectRatio, eventListItemHover } from 'utils/styles.utils';

import { Routes } from 'constants/routes.constants';
import { useLocale } from 'hooks/useLocale';
import { EventCardFragment } from 'types/api/dato-operations.types';

import DatoImage from 'components/@common/DatoImage';
import LinkUnstyled from 'components/@common/LinkUnstyled';
import { formatEventDate } from 'components/@events/utils';

interface Props {
  event: EventCardFragment;
  isPastEvent?: boolean;
  titleComponent?: ElementType;
  borderless?: boolean;
  onClick?: () => void;
}

const EventCard = ({
  event,
  isPastEvent = false,
  titleComponent = 'h3',
  borderless,
  onClick,
}: Props) => {
  const { locale } = useLocale();

  const formattedDate = formatEventDate({
    locale,
    startDate: event.startDatetime,
    endDate: event.endDatetime,
    timeZone: event.timezone,
    isPastEvent,
  });

  return (
    <Stack
      component={LinkUnstyled}
      href={replacePathParams(Routes.EventDetail, { slug: event.slug })}
      onClick={onClick}
      sx={{
        flex: 1,
        overflow: 'hidden',
        borderRadius: borderless ? 0 : 4,
        p: borderless ? 0 : 1,
        border: borderless ? '' : '1px solid',
        borderColor: 'grey.800',
        ...eventListItemHover,
      }}
    >
      <div>
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: '10px',
            ...aspectRatio(1, 1),
          }}
        >
          <DatoImage
            data={event.cover}
            fill
            style={{ objectFit: 'cover' }}
            sizes="512px"
          />
        </Box>
        <Box
          sx={{
            mt: 2,
            mx: borderless ? 0 : 1,
          }}
        >
          <Typography
            variant="h5"
            component={titleComponent}
            sx={{
              mb: 1,
            }}
          >
            {event.title}
          </Typography>
        </Box>
      </div>
      <Stack
        sx={{
          mx: borderless ? 0 : 1,
          mb: borderless ? 0 : 1,
        }}
      >
        <Typography
          variant="extraSmall"
          sx={{
            color: 'global01.400',
          }}
        >
          {formattedDate}
        </Typography>

        <Typography
          variant="extraSmall"
          sx={{
            color: 'text.secondary',
          }}
        >
          <FormattedMessage
            id="event.city.in"
            values={{ city: event.venue.city.name }}
          />
        </Typography>
      </Stack>
    </Stack>
  );
};

export default EventCard;
